import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useJobs } from '../hooks/useJobs';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useServices } from '../../services/hooks/useServices';
import { useProperties } from '../../properties/hooks/useProperties';
import { Property, DateUtils, getBadgeColorClasses, getStatusColor } from '@cleanpro/common-ui';
import JobChecklistInstance from '../JobChecklistInstance';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  DollarSign, 
  Phone, 
  Mail,
  FileText,
  Edit,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  XCircle,
  Home,
  Users,
  ClipboardList
} from 'lucide-react';

export default function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const { jobs, updateJobStatus, updateJob, getJobEvents, fetchJobEvents } = useJobs();
  const { customers } = useCustomers();
  const { services } = useServices();
  const { properties } = useProperties();
  const [ showEditForm, setShowEditForm] = useState(false);
  const [ isEditingJob, setIsEditingJob] = useState(false);
  const [ notes, setNotes] = useState('');
  const [ checklistProgress, setChecklistProgress] = useState<{ completed: number; total: number } | null>(null);
  const [ editForm, setEditForm] = useState({
    customerId: '',
    serviceId: '',
    propertyId: '',
    quoteId: '',
    date: '',
    time: '',
    notes: '',
    price: 0,
    address: ''
  });

  const job = jobs.find(b => b.id === jobId);
  const customer = customers.find(c => c.id === job?.customer?.id);
  const property = properties.find(p => p.id === job?.property?.id);

  // Helper function to get property icon
  const getPropertyIcon = (property: Property | undefined) => {
    if (!property) return <MapPin className="h-5 w-5" />;
    
    switch (property.propertyType) {
      case 'house': return <Home className="h-5 w-5" />;
      case 'apartment': return <Home className="h-5 w-5" />;
      case 'condo': return <Home className="h-5 w-5" />;
      case 'townhouse': return <Home className="h-5 w-5" />;
      case 'office': return <Users className="h-5 w-5" />;
      default: return <Home className="h-5 w-5" />;
    }
  };

  // Helper functions for timeline
  const getEventColor = (eventType: string): string => {
    switch (eventType) {
      case 'created':
        return 'bg-indigo-600';
      case 'updated':
        return 'bg-yellow-500';
      case 'status_changed':
        return 'bg-indigo-500';
      case 'employee_assigned':
      case 'employee_changed':
        return 'bg-purple-500';
      case 'rescheduled':
        return 'bg-orange-500';
      case 'requested':
        return 'bg-purple-600';
      case 'confirmed':
        return 'bg-green-500';
      case 'in_progress':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-600';
      case 'cancelled':
        return 'bg-red-500';
      case 'rated':
        return 'bg-yellow-400';
      case 'feedback_added':
        return 'bg-pink-500';
      case 'payment_received':
        return 'bg-emerald-500';
      case 'refunded':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const formatEventDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} minutes ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday at ' + DateUtils.formatTimeFromDate(date);
    } else {
      return DateUtils.formatDateLong(date) + ' at ' + DateUtils.formatTimeFromDate(date);
    }
  };

  // Fetch job events when component mounts
  useEffect(() => {
    fetchJobEvents();
  }, [fetchJobEvents]);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;

    console.log('Submitting edit form:', editForm);

    try {
      // Combine date and time into a single ISO string
      const dateTime = new Date(`${editForm.date}T${editForm.time}`).toISOString();

      await updateJob(job.id, {
        ...editForm,
        date_time: dateTime,
      });

      setIsEditingJob(false);
      alert('Job updated successfully!');
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Failed to update job. Please try again.');
    }
  };

  const handleEditCancel = () => {
    if (job) {
      const jobDate = new Date(job.date_time);
      setEditForm({
        customerId: job.customer?.id || '',
        serviceId: job.service?.id || '',
        propertyId: job.property?.id || '',
        quoteId: job.quote?.id || '',
        date: jobDate.toISOString().split('T')[0],
        time: jobDate.toTimeString().split(' ')[0].substring(0, 5),
        notes: job.notes || '',
        price: job.price,
        address: `${job.property?.addressLine1 || ''}, ${job.property?.city || ''}, ${job.property?.state || ''} ${job.property?.postalCode || ''}`
      });
    }
    setIsEditingJob(false);
  };

  if (!job) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/jobs" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Job Not Found</h1>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <p className="text-gray-500">The job you're looking for doesn't exist.</p>
          <Link to="/jobs" className="text-indigo-600 hover:text-blue-700 font-medium mt-2 inline-block">
            Return to Jobs
          </Link>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-indigo-600" />;
      case 'confirmed':
        return <Calendar className="h-5 w-5 text-yellow-600" />;
      case 'requested':
        return <AlertCircle className="h-5 w-5 text-purple-600" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const handleStatusUpdate = (newStatus: string) => {
    if (!job) return;
    updateJobStatus(job.id, newStatus as 'requested' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled');
  };

  const handleNotesUpdate = () => {
    // In a real app, you'd update the job notes here
    console.log('Updating notes:', notes);
    setShowEditForm(false);
  };

  return (
    <div className="space-y-6">
      {!job ? (
        /* Job Not Found */
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-gray-400 mb-4">
            <AlertCircle className="h-16 w-16 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h1>
          <p className="text-gray-600 mb-6">
            The job you're looking for doesn't exist or may have been deleted.
          </p>
          <Link 
            to="/jobs" 
            className="inline-flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Jobs</span>
          </Link>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/jobs" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{job.service?.name} - {job.customer?.name}</h1>
                <p className="text-gray-600">Job #{job.id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${getBadgeColorClasses(getStatusColor(job.status, 'job'))} border-current`}>
                {getStatusIcon(job.status)}
                <span className="font-medium capitalize">{job.status.replace('-', ' ')}</span>
              </div>
              <button 
                onClick={() => setIsEditingJob(true)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Job</span>
              </button>
            </div>
          </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Service Information */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            {isEditingJob ? (
              /* Edit Form */
              <form onSubmit={handleEditSubmit} className="space-y-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Job</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                    <select
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={editForm.customerId}
                      onChange={(e) => setEditForm({ ...editForm, customerId: e.target.value })}
                    >
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                    <select
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={editForm.serviceId}
                      onChange={(e) => setEditForm({ ...editForm, serviceId: e.target.value })}
                    >
                      {services.map(service => (
                        <option key={service.id} value={service.id}>{service.name} - ${service.basePrice}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={editForm.date}
                      onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={editForm.time}
                      onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              /* Display Mode */
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">{job.service?.name}</h3>
                    <p className="text-gray-600 mb-4">{job.service?.description || 'No description available'}</p>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Duration: {job.service?.duration} hours</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Price: ${job.price}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Schedule</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {DateUtils.formatDateLong(job.date_time)} 
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {DateUtils.formatTimeFromDate(job.date_time)} 
                        </span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                        <span className="text-sm text-gray-600">{`${job.property?.addressLine1}, ${job.property?.city}, ${job.property?.state} ${job.property?.postalCode}`}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Customer Information */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Customer Information</h2>
              <Link 
                to={`/customers/${job.customer?.id}`}
                className="text-indigo-600 hover:text-blue-700 font-medium text-sm"
              >
                View Full Profile →
              </Link>
            </div>
            <div className="flex items-start space-x-4">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-lg font-medium text-blue-700">
                  {job.customer?.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{job.customer?.name}</h3>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{customer?.email || 'No email on file'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{customer?.phone || 'No phone on file'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Assignment */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Assigned Business</h2>
              <Link 
                to={`/businesses/${job.business?.id}`}
                className="text-indigo-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
              >
                <span>View Business →</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-lg font-medium text-green-700">
                  {job.business?.businessName.charAt(0)}
                </span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{job.business?.businessName}</h3>
                {/* <p className="text-sm text-gray-600">{job.owner_name || 'Cleaning Specialist'}</p> */}
                {/* <div className="flex items-center space-x-2 mt-1">
                  <Phone className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500">{employee?.phone || 'Contact via office'}</span>
                </div> */}
              </div>
            </div>
          </div>

          {/* Property Information */}
          {property && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Property Information</h2>
                <Link 
                  to={`/properties/${property.id}`}
                  className="text-indigo-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
                >
                  <span>View Property →</span>
                </Link>
              </div>
              <div className="flex items-start space-x-4">
                <div className="text-indigo-600">
                  {getPropertyIcon(property)}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">
                    {property.addressLine1}<br />
                    {property.addressLine2 && <>{property.addressLine2}<br /></>}
                    {property.city}, {property.state} {property.postalCode}
                    </h3>
                  <p className="text-sm text-gray-600 capitalize mb-2">{property.propertyType}</p>
                  
                  {/* Property Details */}
                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    {property.squareFootage && (
                      <div className="text-gray-600">
                        <span className="font-medium">{property.squareFootage.toLocaleString()}</span> sq ft
                      </div>
                    )}
                    {property.bedrooms && property.bedrooms > 0 && (
                      <div className="text-gray-600">
                        <span className="font-medium">{property.bedrooms}</span> bedrooms
                      </div>
                    )}
                    {property.bathrooms && property.bathrooms > 0 && (
                      <div className="text-gray-600">
                        <span className="font-medium">{property.bathrooms}</span> bathrooms
                      </div>
                    )}
                    {property.floors && (
                      <div className="text-gray-600">
                        <span className="font-medium">{property.floors}</span> floors
                      </div>
                    )}
                  </div>
                  
                  {/* Special Notes */}
                  {(property.hasPets || property.accessInstructions || property.specialInstructions) && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Special Notes</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        {property.hasPets && (
                          <div className="flex items-center space-x-2">
                            <span className="text-amber-600">🐾</span>
                            <span>Pets on property</span>
                            {property.petDetails && <span className="text-xs">({property.petDetails})</span>}
                          </div>
                        )}
                        {property.accessInstructions && (
                          <div className="flex items-start space-x-2">
                            <span className="text-indigo-600">🔑</span>
                            <span>{property.accessInstructions}</span>
                          </div>
                        )}
                        {property.specialInstructions && (
                          <div className="flex items-start space-x-2">
                            <span className="text-purple-600">📝</span>
                            <span>{property.specialInstructions}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notes and Comments */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Notes & Comments</h2>
              <button 
                onClick={() => setShowEditForm(!showEditForm)}
                className="text-indigo-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1"
              >
                <MessageSquare className="h-4 w-4" />
                <span>Add Note</span>
              </button>
            </div>
            
            {job.notes ? (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Customer Notes</h4>
                <p className="text-gray-700">{job.notes}</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm mb-4">No customer notes provided</p>
            )}

            {showEditForm && (
              <div className="border-t pt-4">
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Add internal notes about this job..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <div className="flex justify-end space-x-2 mt-2">
                  <button 
                    onClick={() => setShowEditForm(false)}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleNotesUpdate}
                    className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-blue-700"
                  >
                    Save Note
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Job Checklist */}
          {(job.status === 'confirmed' || job.status === 'in-progress' || job.status === 'completed') && (
            <JobChecklistInstance
              jobId={job.id}
              jobStatus={job.status}
              serviceId={job.service?.id || ''}
              serviceName={job.service?.name || ''}
              customerName={job.customer?.name || ''}
              onUpdateProgress={setChecklistProgress}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Management */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Management</h3>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Update Status</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={job.status}
                onChange={(e) => handleStatusUpdate(e.target.value)}
              >
                <option value="requested">Requested</option>
                <option value="confirmed">Confirmed</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Job Checklist Progress */}
          {checklistProgress && (job.status === 'confirmed' || job.status === 'in-progress' || job.status === 'completed') && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <ClipboardList className="h-5 w-5 text-indigo-600" />
                <span>Job Checklist</span>
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-gray-900">
                    {checklistProgress.completed}/{checklistProgress.total} tasks
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${checklistProgress.total > 0 ? (checklistProgress.completed / checklistProgress.total) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 text-center">
                  {checklistProgress.completed === checklistProgress.total 
                    ? 'All tasks completed!' 
                    : `${checklistProgress.total - checklistProgress.completed} tasks remaining`
                  }
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <span>Call Customer</span>
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>Send Email</span>
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Generate Invoice</span>
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-2">
                <XCircle className="h-4 w-4" />
                <span>Cancel Job</span>
              </button>
            </div>
          </div>

          {/* Relationships */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                Relationships
              </h3>
            </div>
            <div className="p-6 space-y-4">

             <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-700">Quote</label>
                  <p className="text-sm text-gray-600 mt-1">
                  {job.quote?.id ? (
                    <Link to={`/quotes/${job.quote.id}`}>Quote #{job.quote.quoteNumber}</Link>
                  ) : (
                    'No quote associated'
                  )}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-700">Request</label>
                  <p className="text-sm text-gray-600 mt-1">
                  {/* <Link to={`/requests/${job.request.id}`}>Request #{job.request.id}</Link> */}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-700">Job</label>
                  <p className="text-sm text-gray-600 mt-1">
                    <Link to={`/jobs/${job.id}`}>Job #{job.id} (This page)</Link>
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-700">Customer</label>
                  <p className="text-sm text-gray-600 mt-1">
                  {job.customer?.id ? (
                    <Link to={`/customers/${job.customer.id}`}>{job.customer.id}</Link>
                  ) : (
                    'No customer associated'
                  )}
                  </p>
                </div>
              </div>

               <div className="flex items-start space-x-3 pt-2 border-t border-gray-100">
                <div className="flex-shrink-0 w-2 h-2 bg-gray-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-700">Property</label>
                  <p className="text-sm text-gray-600 mt-1">
                  {job.property?.id ? (
                    <Link to={`/properties/${job.property.id}`}>{job.property.id}</Link>
                  ) : (
                    'No property associated'
                  )}
                  </p>
                </div>
              </div>

            </div>
          </div>


          {/* Job Timeline */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-3">
              {getJobEvents(jobId || '').length > 0 ? (
                getJobEvents(jobId || '').map((event, index) => (
                  <div key={event.id} className="flex items-start space-x-3">
                    <div 
                      className={`h-2 w-2 rounded-full mt-2 ${
                        index === 0 
                          ? 'bg-indigo-600' 
                          : getEventColor(event.eventType)
                      }`}
                    ></div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        index === 0 ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {event.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatEventDate(event.createdAt)} by {event.userName}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-start space-x-3">
                  <div className="h-2 w-2 bg-indigo-600 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Job Created</p>
                    <p className="text-xs text-gray-500">
                      {new Date(job.date_time).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}